import React, { useEffect, useMemo, useState } from 'react';
import {
  getTimeLogs,
  createTimeLog,
  updateTimeLog,
  deleteTimeLog,
  exportTimeCSV,
} from '../services/timeService';
import { getProjects } from '../services/projectService';
import './TimePage.css';

const toHourString = (mins) => (Number(mins || 0) / 60).toFixed(2);

export default function TimePage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    projectId: '',
    hours: '',
    description: '',
    billable: true,
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    getProjects()
      .then((res) => setProjects(res.data.projects || []))
      .catch((err) => console.error('Failed to load projects:', err));
  }, []);

  useEffect(() => {
    loadLogs();
  }, [filterYear]); // eslint-disable-line

  const loadLogs = () => {
    setLoading(true);
    getTimeLogs({ year: filterYear })
      .then((res) => setLogs(res.data.logs || []))
      .catch((err) => console.error('Failed to load time logs:', err))
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setForm({
      date: new Date().toISOString().split('T')[0],
      projectId: '',
      hours: '',
      description: '',
      billable: true,
    });
    setFormError('');
    setEditingLog(null);
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (log) => {
    setEditingLog(log);
    setForm({
      date: new Date(log.date).toISOString().split('T')[0],
      projectId: log.projectId?._id || log.projectId || '',
      hours: toHourString(log.minutes),
      description: log.description || '',
      billable: log.billable !== false,
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.projectId) {
      setFormError('Project is required.');
      return;
    }

    const hoursNum = parseFloat(form.hours);
    if (Number.isNaN(hoursNum) || hoursNum <= 0) {
      setFormError('Hours must be greater than 0.');
      return;
    }

    const minutes = Math.round(hoursNum * 60);

    setSaving(true);
    setFormError('');

    const payload = {
      date: form.date,
      projectId: form.projectId,
      minutes,
      description: form.description.trim(),
      billable: form.billable,
    };

    try {
      if (editingLog) {
        const res = await updateTimeLog(editingLog._id, payload);
        setLogs((prev) => prev.map((l) => (l._id === editingLog._id ? res.data.log : l)));
      } else {
        const res = await createTimeLog(payload);
        setLogs((prev) => [res.data.log, ...prev]);
      }
      setShowForm(false);
      resetForm();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Failed to save time log.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await deleteTimeLog(logId);
      setLogs((prev) => prev.filter((l) => l._id !== logId));
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete time log');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportTimeCSV(filterYear);
    } catch (_) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const logsByMonth = useMemo(() => {
    const grouped = {};
    [...logs]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .forEach((log) => {
        const d = new Date(log.date);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
        if (!grouped[key]) grouped[key] = { label, logs: [], totalMinutes: 0 };
        grouped[key].logs.push(log);
        grouped[key].totalMinutes += log.minutes || 0;
      });
    return Object.values(grouped);
  }, [logs]);

  const yearlyTotalMinutes = useMemo(
    () => logs.reduce((sum, l) => sum + (l.minutes || 0), 0),
    [logs]
  );

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) yearOptions.push(y);

  return (
    <div className="time-page">
      <div className="time-header">
        <div className="time-header-left">
          <h1 className="time-title">⏱ Time Tracking</h1>
          <p className="time-subtitle">Track hours by project and employee-ready entries for reporting.</p>
        </div>
        <div className="time-header-controls">
          <select
            className="time-year-select"
            value={filterYear}
            onChange={(e) => setFilterYear(Number(e.target.value))}
          >
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-ghost btn-sm" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : `⬇ Export CSV ${filterYear}`}
          </button>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>+ Log Time</button>
        </div>
      </div>

      {showForm && (
        <div className="time-form-card">
          <h3 className="time-form-title">{editingLog ? 'Edit Time Entry' : 'Log Time Entry'}</h3>
          <form onSubmit={handleSubmit} className="time-form">
            <div className="time-form-row">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.date}
                  onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Project *</label>
                <select
                  className="form-select"
                  value={form.projectId}
                  onChange={(e) => setForm((p) => ({ ...p, projectId: e.target.value }))}
                  required
                >
                  <option value="">Select project</option>
                  {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Hours *</label>
                <input
                  type="number"
                  className="form-input"
                  step="0.25"
                  min="0.25"
                  placeholder="e.g. 1.5"
                  value={form.hours}
                  onChange={(e) => setForm((p) => ({ ...p, hours: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <input
                className="form-input"
                placeholder="What work was completed?"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>

            <label className="time-billable-toggle">
              <input
                type="checkbox"
                checked={form.billable}
                onChange={(e) => setForm((p) => ({ ...p, billable: e.target.checked }))}
              />
              Billable entry
            </label>

            {formError && <p className="form-error">{formError}</p>}

            <div className="form-row">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : editingLog ? 'Update' : 'Save Entry'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => { setShowForm(false); resetForm(); }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {!loading && (
        <div className="time-yearly-total">
          <span className="time-total-label">{filterYear} Total:</span>
          <span className="time-total-value">{toHourString(yearlyTotalMinutes)} hrs</span>
        </div>
      )}

      {loading ? (
        <p className="panel-muted">Loading time logs…</p>
      ) : logsByMonth.length === 0 ? (
        <p className="panel-muted">No entries for {filterYear}. Click "+ Log Time" to add one.</p>
      ) : (
        logsByMonth.map((month) => (
          <div key={month.label} className="time-month-group">
            <div className="time-month-header">
              <span className="time-month-label">{month.label}</span>
              <span className="time-month-total">{toHourString(month.totalMinutes)} hrs</span>
            </div>
            <table className="time-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Hours</th>
                  <th>Billable</th>
                  <th>Description</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {month.logs.map((log) => (
                  <tr key={log._id}>
                    <td data-label="Date">{new Date(log.date).toLocaleDateString('en-CA')}</td>
                    <td data-label="Project">{log.projectId?.name || '—'}</td>
                    <td data-label="Hours" className="time-hours-cell">{toHourString(log.minutes)}</td>
                    <td data-label="Billable">{log.billable ? 'Yes' : 'No'}</td>
                    <td data-label="Description">{log.description || '—'}</td>
                    <td data-label="Actions" className="time-actions">
                      <button className="btn-icon" title="Edit" onClick={() => openEdit(log)}>✏</button>
                      <button className="btn-icon-danger" title="Delete" onClick={() => handleDelete(log._id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
